'use client'

import { useEffect, useState } from 'react'
import { getCart } from '@/lib/cart'
import CartItem from '@/components/CartItem'
import OrderRequestForm from '@/components/OrderRequestForm'
import type { CartItem as CartItemType } from '@/lib/types'

export default function CartPage() {
  const [cartItems, setCartItems] = useState<CartItemType[]>([])
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    setCartItems(getCart())

    const handleCartUpdate = () => {
      setCartItems(getCart())
    }

    window.addEventListener('cartUpdated', handleCartUpdate)
    return () => window.removeEventListener('cartUpdated', handleCartUpdate)
  }, [])

  if (!mounted) {
    return <div className="max-w-7xl mx-auto px-4 py-8">Loading...</div>
  }

  const total = cartItems.reduce((sum, item) => sum + item.priceCents * item.quantity, 0)

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="bg-white rounded-3xl shadow-soft p-8 mb-10">
        <h1 className="text-4xl font-bold mb-2">Your Cart 🧺</h1>
        <p className="text-gray-600">
          Squish prints each order after it’s placed and payment has been received via Venmo or cash.
          Once your item is printed, you’ll receive an email letting you know it’s ready for pickup or delivery.
        </p>
      </div>

      {cartItems.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-3xl shadow-soft">
          <p className="text-gray-500 text-lg mb-4">Your cart is empty</p>
          <a
            href="/shop"
            className="inline-block bg-primary text-white px-6 py-3 rounded-full shadow-soft hover:shadow-pop transition-all"
          >
            Browse Products
          </a>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => (
              <CartItem
                key={`${item.productId}-${item.colorId}`}
                item={item}
                onUpdate={() => setCartItems(getCart())}
              />
            ))}

            {/* Cart Total */}
            <div className="bg-white p-6 rounded-3xl shadow-soft">
              <div className="flex justify-between items-center text-xl font-bold">
                <span>Order Total:</span>
                <span className="text-primary">${(total / 100).toFixed(2)}</span>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                This is an informational total. Payment will be collected at pickup/delivery.
              </p>
            </div>
          </div>

          {/* Order Request Form */}
          <div className="lg:col-span-1">
            <OrderRequestForm cartItems={cartItems} total={total} />
          </div>
        </div>
      )}
    </div>
  )
}
